'use client';

import { useState } from 'react';
import { Plus, Edit2, Trash2, BookOpen } from 'lucide-react';
import { CategoryCard } from '@/components/category-card';
import { Header } from '@/components/header';
import { categories } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Category } from '@/lib/types';
import * as Icons from 'lucide-react';

const iconOptions = [
  'BookOpen', 'Cpu', 'Landmark', 'TrendingUp', 'GraduationCap', 
  'Coffee', 'Palette', 'Globe', 'Heart', 'Star', 'Music', 'Camera'
];

const colorOptions = [
  { name: '蓝色', value: '#3B82F6' },
  { name: '绿色', value: '#10B981' },
  { name: '紫色', value: '#8B5CF6' },
  { name: '橙色', value: '#F59E0B' },
  { name: '红色', value: '#EF4444' },
  { name: '青色', value: '#64FFDA' },
  { name: '粉色', value: '#EC4899' },
  { name: '靛蓝', value: '#6366F1' },
];

export default function CategoriesPage() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const handleView = (category: Category) => {
    console.log('View category:', category);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (category: Category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const totalBooks = categories.reduce((sum, cat) => sum + cat.bookCount, 0);

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="分类管理" 
        subtitle={`共 ${categories.length} 个分类，${totalBooks} 本图书`}
        onAdd={() => setIsAddDialogOpen(true)}
      />

      <div className="p-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {categories.slice(0, 4).map((cat) => {
            const IconComponent = (Icons as Record<string, React.ComponentType<{ className?: string }>>)[cat.icon] || Icons.BookOpen;
            return (
              <div 
                key={cat.id} 
                className="p-4 rounded-xl bg-[#112240] border border-[#1d3557] flex items-center gap-3"
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
                >
                  <IconComponent className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-[#8892B0]">{cat.name}</p>
                  <p className="text-lg font-semibold text-[#E6F1FF]">{cat.bookCount}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onView={handleView}
              onEdit={handleEdit}
            />
          ))}
          
          {/* Add New Card */}
          <button
            onClick={() => setIsAddDialogOpen(true)}
            className="group p-6 rounded-xl bg-[#112240]/50 border border-dashed border-[#1d3557] hover:border-[#64FFDA]/50 hover:bg-[#112240] transition-all flex flex-col items-center justify-center min-h-[200px]"
          >
            <div className="w-14 h-14 rounded-xl bg-[#64FFDA]/10 flex items-center justify-center group-hover:bg-[#64FFDA]/20 transition-colors">
              <Plus className="w-7 h-7 text-[#64FFDA]" />
            </div>
            <p className="mt-4 text-[#8892B0] group-hover:text-[#E6F1FF] transition-colors">添加新分类</p>
          </button>
        </div>
      </div>

      {/* Add Category Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-lg">
          <DialogHeader>
            <DialogTitle>添加分类</DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              创建新的图书分类
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>分类名称</Label>
              <Input className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="输入分类名称" />
            </div>
            <div className="space-y-2">
              <Label>分类描述</Label>
              <Textarea className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" placeholder="输入分类描述" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>图标</Label>
                <Select>
                  <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                    <SelectValue placeholder="选择图标" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-[#1d3557]">
                    {iconOptions.map((icon) => {
                      const IconComponent = (Icons as Record<string, React.ComponentType<{ className?: string }>>)[icon];
                      return (
                        <SelectItem key={icon} value={icon}>
                          <div className="flex items-center gap-2">
                            {IconComponent && <IconComponent className="w-4 h-4" />}
                            <span>{icon}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>颜色</Label>
                <Select>
                  <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                    <SelectValue placeholder="选择颜色" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-[#1d3557]">
                    {colorOptions.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: color.value }} />
                          <span>{color.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsAddDialogOpen(false)}>
              取消
            </Button>
            <Button className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90">
              添加分类
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-lg">
          <DialogHeader>
            <DialogTitle>编辑分类</DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              修改分类信息
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>分类名称</Label>
              <Input 
                className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                placeholder="输入分类名称"
                defaultValue={selectedCategory?.name}
              />
            </div>
            <div className="space-y-2">
              <Label>分类描述</Label>
              <Textarea 
                className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                placeholder="输入分类描述"
                defaultValue={selectedCategory?.description}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>图标</Label>
                <Select defaultValue={selectedCategory?.icon}>
                  <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-[#1d3557]">
                    {iconOptions.map((icon) => {
                      const IconComponent = (Icons as Record<string, React.ComponentType<{ className?: string }>>)[icon];
                      return (
                        <SelectItem key={icon} value={icon}>
                          <div className="flex items-center gap-2">
                            {IconComponent && <IconComponent className="w-4 h-4" />}
                            <span>{icon}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>颜色</Label>
                <Select defaultValue={selectedCategory?.color}>
                  <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-[#1d3557]">
                    {colorOptions.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: color.value }} />
                          <span>{color.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
              onClick={() => {
                setIsEditDialogOpen(false);
                setIsDeleteDialogOpen(true);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              删除
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsEditDialogOpen(false)}>
                取消
              </Button>
              <Button className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90">
                保存修改
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#EF4444] flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              确认删除
            </DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              确定要删除分类「{selectedCategory?.name}」吗？此操作不可恢复。
              {selectedCategory && selectedCategory.bookCount > 0 && (
                <p className="mt-2 text-[#F59E0B]">
                  注意：该分类下有 {selectedCategory.bookCount} 本图书，删除前请先转移图书。
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsDeleteDialogOpen(false)}>
              取消
            </Button>
            <Button 
              className="bg-[#EF4444] text-white hover:bg-[#EF4444]/90"
              disabled={selectedCategory ? selectedCategory.bookCount > 0 : false}
            >
              确认删除
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
